<template>
  <div class="mt-12 md:mt-0 md:col-span-6 lg:col-span-4 md:mt-0 md:pl-14 md:pr-14">
    <div class="flex items-center">
      <CardTitle class="flex-auto">{{ formatDate(currentMonth, 'MMMM yyyy') }}</CardTitle>
      <Button variant="ghost" size="icon" @click="previousMonth()">
        <span class="sr-only">Previous month</span>
        <vue-feather type="chevron-left" class="h-5 w-5" />
      </Button>
      <Button variant="ghost" size="icon" @click="nextMonth()">
        <span class="sr-only">Next month</span>
        <vue-feather type="chevron-right" class="h-5 w-5" />
      </Button>
    </div>
    <div class="mt-10 grid grid-cols-7 text-center text-xs leading-6 text-gray-500">
      <div>S</div>
      <div>M</div>
      <div>T</div>
      <div>W</div>
      <div>T</div>
      <div>F</div>
      <div>S</div>
    </div>
    <div class="mt-2 grid grid-cols-7 text-sm">
      <div v-for="day of calendarDays" :key="day.toString()" class="py-2">
        <button
          type="button"
          :class="[
            'day-btn',
            isDateSame(day, currentDay, 'day')
              ? 'day-btn--selected'
              : hasAvailableSlots(day)
                ? 'day-btn--available'
                : 'day-btn--default',
          ]"
          @click="
            currentDay = day;
            emit('selectDay', day);
          "
        >
          {{ formatDate(day, 'd') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { addMonths, startOfMonth, startOfWeek, endOfMonth, endOfWeek } from 'date-fns';
import { Button } from '@/components/ui/button';
import { CardTitle } from '@/components/ui/card';
import DateUtility from '../../utilities/dates';
import type { EventSlot } from './useInterviewBooking';

const props = defineProps<{
  eventSlots: EventSlot[];
}>();

const emit = defineEmits<{
  selectDay: [day: Date];
}>();

const { formatDate, isDateSame } = DateUtility;

const calendarDays = ref<Date[]>([]);
const currentMonth = ref(new Date());
const currentDay = ref(new Date());

const getDays = () => {
  const firstDayOfMonth = startOfMonth(currentMonth.value);
  const firstDayOfCal = startOfWeek(firstDayOfMonth);
  const lastDayOfMonth = endOfMonth(firstDayOfMonth);
  const lastDayOfCal = endOfWeek(lastDayOfMonth);
  const temp = new Date(firstDayOfCal);
  const days = [new Date(temp)];
  while (temp < lastDayOfCal && days.length < 42) {
    temp.setDate(temp.getDate() + 1);
    days.push(new Date(temp));
  }
  while (days.length < 42) {
    temp.setDate(temp.getDate() + 1);
    days.push(new Date(temp));
  }
  calendarDays.value = days;
};

const nextMonth = () => {
  currentMonth.value = addMonths(currentMonth.value, 1);
  getDays();
};

const previousMonth = () => {
  currentMonth.value = addMonths(currentMonth.value, -1);
  getDays();
};

const hasAvailableSlots = (d: Date) => {
  return props.eventSlots.some(slot => isDateSame(new Date(slot.startTime), d, 'day'));
};

onMounted(() => {
  getDays();
});
</script>

<style scoped>
@reference 'tailwindcss';

.day-btn {
  @apply mx-auto flex h-8 w-8 items-center justify-center rounded-full;
}

.day-btn--selected {
  @apply bg-gray-900 text-white;
}

.day-btn--available {
  @apply bg-indigo-600 text-white hover:bg-gray-200;
}

.day-btn--default {
  @apply text-gray-900 hover:bg-gray-200;
}
</style>
